// M005-F0052 (M005-D005 §4.2/§7.2) — Bookings Hub series grouping.
//
// DIRECT COPY of src/components/event-simple/myBookingsGrouping.js as it
// stands on feat/my-bookings-readable (parked MR !334), 2026-09-07, ported
// per §7.2's copy-not-reference ruling: only this provenance header and the
// import specifiers were adapted — every function body is byte-identical to
// the source. The simple view keeps its own untouched copy; if !334 ever
// lands, a follow-up de-duplication ticket reconciles the two (§7.2).
//
// The grouping is already general (it takes any booking array and knows
// nothing about "one person"), so the hub's Upcoming/Past tabs use it as-is
// for the actor's own bookings, and the Studio tab (M005-F0053, stacking
// ticket) applies the same grouping to the org-wide set — one card per
// series on every page that shows a booking series (§6.0).
//
// — — — original header (M009-F014 — "My Bookings" rework, WEB-EVS-014..018)
//
// Owner's words, verbatim, seen on production 2026-09-07 (teacher of
// organisation 44, "Swing Dance School NI", user 312):
//   "The booking page is very confusing... this need to reworked to make
//   it understandable"
//
// Spec: studioaeye-backend/docs/design/M009-Event-Simple-User-Type.md,
// section "M009-F014: Readable My Bookings".
//
// This module is the pure, unit-testable heart of the rework: it turns the
// flat list GET /api/bookings/my already returns into
//   - an Upcoming section (soonest first) and a Past section (newest
//     first), split at `now`;
//   - ONE card per recurring series (grouped the same way
//     adminBookingsGrouping.js / Dashboard.js / BookRoomForm.js already
//     group series elsewhere in this app — same google_recurring_event_id
//     convention, same MASTER-row-with-no-id fallback), carrying its own
//     from/until dates, its next occurrence, and the full per-date list;
//   - plain-language formatting for single bookings and series headlines.
//
// No new data is invented: every field used here (organisation_name,
// room_name, status, approval_status, sync_status, booking_type,
// google_recurring_event_id, google_recurrence_rule) is already returned
// by GET /api/bookings (studioaeye-backend src/services/booking.service.ts
// listBookings: `SELECT b.*, r.name as room_name, o.name as
// organisation_name, ...`). The per-date list for a bounded/open-ended
// series is computed client-side from the RRULE (expandOccurrences) and
// overlaid with any real per-date exception rows
// (booking_type='recurring_instance') already present in the same
// response — never fabricated.
import {
  getStatusBadgeClass, getStatusText, resolveDisplayStatus,
} from './bookingStatus';
import { expandOccurrences, describeSeriesPattern } from '../../utils/recurrenceParser';

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad2 = (n) => String(n).padStart(2, '0');

const isValidDate = (d) => d instanceof Date && !Number.isNaN(d.getTime());

const sameCalendarDate = (a, b) => isValidDate(a) && isValidDate(b)
  && a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate();

const formatTime = (d) => (isValidDate(d) ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}` : '');

// "4 Aug 2026" — used inside a series' "from ... until ..." summary, where
// the day-of-week is already established by the pattern label ("Every
// Tuesday").
const formatPlainDate = (d) => (isValidDate(d)
  ? `${d.getDate()} ${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`
  : '');

// "Tue 4 Aug 2026" — used for a single (non-recurring) booking's own card,
// and for each row of a series' expandable per-date list.
const formatShortDate = (d) => (isValidDate(d)
  ? `${WEEKDAY_SHORT[d.getDay()]} ${d.getDate()} ${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`
  : '');

// "Tue 29 Sep" — no year — for the page-level "Next: ..." heading, which
// is always near-future and where the year adds nothing.
const formatShortDateNoYear = (d) => (isValidDate(d)
  ? `${WEEKDAY_SHORT[d.getDay()]} ${d.getDate()} ${MONTH_SHORT[d.getMonth()]}`
  : '');

// Decision 3: redundant date repetition ("04 Aug 2026, 19:00 - 04 Aug
// 2026, 21:30") becomes "Tue 4 Aug 2026 · 19:00–21:30" — the date is
// repeated ONLY when the booking spans midnight (a real, if rare, shape:
// an overnight studio hire).
export const formatSingleBookingWhen = (start, end) => {
  if (!isValidDate(start)) return '';
  if (isValidDate(end) && !sameCalendarDate(start, end)) {
    return `${formatShortDate(start)} ${formatTime(start)} – ${formatShortDate(end)} ${formatTime(end)}`;
  }
  return isValidDate(end)
    ? `${formatShortDate(start)} · ${formatTime(start)}–${formatTime(end)}`
    : `${formatShortDate(start)} · ${formatTime(start)}`;
};

// Same convention (BookRoomForm.js's getSeriesKey / adminBookingsGrouping's
// / Dashboard.js's groupRecurringBookings): prefer the real
// google_recurring_event_id; fall back to a deterministic key so a
// Google-imported MASTER row (google_recurring_event_id NULL, but its own
// google_recurrence_rule set) or a legacy/native recurring booking still
// groups instead of falling into "not recurring".
export const buildSeriesKey = (booking) => {
  if (!booking) return null;
  if (booking.google_recurring_event_id) return `gcal:${booking.google_recurring_event_id}`;
  if (booking.booking_type === 'recurring' || booking.booking_type === 'recurring_instance') {
    return `fallback:${booking.room_id || 'room'}:${booking.title || 'untitled'}`;
  }
  return null;
};

const decorateOccurrence = (occurrence, sourceBooking, isException) => ({
  start: occurrence.start,
  end: occurrence.end,
  sourceBooking,
  isException: Boolean(isException),
  statusKey: resolveDisplayStatus(sourceBooking).key,
  statusText: getStatusText(sourceBooking),
  statusBadgeClass: getStatusBadgeClass(sourceBooking),
});

// Build one series card's worth of data from every booking row sharing a
// series key. Two real shapes exist in this app's data (see
// adminBookingsGrouping.js's own header comment for the same split):
//   (a) a single MASTER row carrying its own google_recurrence_rule — the
//       per-date list is computed from the rule (expandOccurrences);
//   (b) one or more materialised 'recurring_instance' rows with no rule of
//       their own — those rows themselves ARE the whole occurrence list,
//       nothing computed.
// A mix of both (a rule-bearing master PLUS separate exception rows for
// individually modified/cancelled dates) overlays the exception rows'
// real status onto the matching computed date instead of the computed
// baseline — this is how "one date cancelled" surfaces per-date rather
// than being hidden behind the series' overall status.
// `export` added by M005-F0055 (WebUI #77): the Calendar tab reuses this
// exact entry builder for week/month occurrence expansion (§8) — the same
// proven code path, never a second implementation. Body byte-identical to
// the !340 version.
export const buildSeriesEntry = (seriesBookings) => {
  const withRule = seriesBookings.find((b) => b.google_recurrence_rule);
  const representative = withRule
    || [...seriesBookings].sort((a, b) => new Date(a.start_time) - new Date(b.start_time))[0];
  const others = seriesBookings.filter((b) => b !== representative);

  let occurrences;
  let isOpenEnded = false;
  let isTruncated = false;

  if (representative.google_recurrence_rule) {
    const expanded = expandOccurrences(
      representative.google_recurrence_rule,
      representative.start_time,
      representative.end_time,
    );
    isOpenEnded = expanded.isOpenEnded;
    isTruncated = expanded.isTruncated;
    occurrences = expanded.occurrences.map((o) => decorateOccurrence(o, representative, false));

    others.forEach((exceptionBooking) => {
      const exStart = new Date(exceptionBooking.start_time);
      const exEnd = exceptionBooking.end_time ? new Date(exceptionBooking.end_time) : null;
      const idx = occurrences.findIndex((o) => sameCalendarDate(o.start, exStart));
      const exceptionOccurrence = decorateOccurrence({ start: exStart, end: exEnd }, exceptionBooking, true);
      if (idx >= 0) {
        occurrences[idx] = exceptionOccurrence;
      } else {
        // The rule's own expansion didn't produce this date (cap, or the
        // exception moved the date) — keep the real row rather than drop it.
        occurrences.push(exceptionOccurrence);
      }
    });
    occurrences.sort((a, b) => a.start - b.start);
  } else {
    occurrences = seriesBookings
      .slice()
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
      .map((b) => decorateOccurrence(
        { start: new Date(b.start_time), end: b.end_time ? new Date(b.end_time) : null },
        b,
        false,
      ));
  }

  return {
    representative, occurrences, isOpenEnded, isTruncated,
  };
};

// The next occurrence to headline: the first whose start hasn't happened
// yet, or — once every occurrence is in the past — the series' own last
// occurrence, explicitly marked ended (same convention as
// getNextOccurrence in recurrenceParser.js).
const pickNextOccurrence = (occurrences, now) => {
  const next = occurrences.find((o) => o.start.getTime() >= now.getTime());
  if (next) return { occurrence: next, isEnded: false };
  const last = occurrences[occurrences.length - 1] || null;
  return { occurrence: last, isEnded: true };
};

// Decision 2: "Every Tuesday, 18:00–22:00 · DAH Studio 1 · from 29 Sep
// 2026 until 22 Sep 2026 / until further notice", aligned with the Term
// Planner's own weekly-slot-with-start/end-dates wording
// (TeacherTermBookings.js's formatDateRange).
export const formatSeriesHeadline = (series) => {
  const first = series.occurrences[0];
  const last = series.occurrences[series.occurrences.length - 1];
  if (!first) return '';
  const pattern = describeSeriesPattern(series.representative.google_recurrence_rule, series.representative.start_time)
    || 'Recurring series';
  const timeLabel = isValidDate(first.end) ? `${formatTime(first.start)}–${formatTime(first.end)}` : formatTime(first.start);
  const until = series.isOpenEnded ? 'until further notice' : `until ${formatPlainDate(last.start)}`;
  const roomFragment = series.roomName ? ` · ${series.roomName}` : '';
  return `${pattern}, ${timeLabel}${roomFragment} · from ${formatPlainDate(first.start)} ${until}`;
};

export const formatOccurrenceRow = (occurrence) => {
  const when = isValidDate(occurrence.end)
    ? `${formatShortDate(occurrence.start)} · ${formatTime(occurrence.start)}–${formatTime(occurrence.end)}`
    : `${formatShortDate(occurrence.start)} · ${formatTime(occurrence.start)}`;
  return `${when} — ${occurrence.statusText}`;
};

// Decision 1: "Next: Tue 29 Sep, 18:00–22:00, DAH Studio 1" for the
// page-level heading.
export const formatNextHeadline = (card) => {
  if (!card) return null;
  const occurrence = card.kind === 'series' ? card.nextOccurrence : { start: card.start, end: card.end };
  if (!occurrence || !isValidDate(occurrence.start)) return null;
  const timeLabel = isValidDate(occurrence.end)
    ? `${formatTime(occurrence.start)}–${formatTime(occurrence.end)}`
    : formatTime(occurrence.start);
  return `Next: ${formatShortDateNoYear(occurrence.start)}, ${timeLabel}, ${card.roomName}`;
};

// Decision 6: the header must show the person, with the organisation as
// context, never the organisation standing in for the person. There is no
// field carrying a "real name distinct from the account's own display
// name" anywhere this page's data reaches (booking rows carry
// organisation_name and user_name — user_name is the same display_name
// column as the logged-in user's own displayName). Organisation context
// therefore comes from the bookings themselves (booking.organisation_name)
// rather than being invented, and is shown only when it says something the
// welcome line doesn't already say.
export const pickOrganisationContext = (bookings, displayName) => {
  const withOrg = (Array.isArray(bookings) ? bookings : []).find((b) => b?.organisation_name);
  const orgName = withOrg?.organisation_name || null;
  if (!orgName) return null;
  if (displayName && orgName.trim().toLowerCase() === String(displayName).trim().toLowerCase()) {
    // The account's own display name already IS the organisation name for
    // this user (a data-quality issue on the account, not something the UI
    // can invent a fix for) — showing it twice would be the same
    // redundancy this rework is removing elsewhere. Drop it per decision 6's
    // explicit "(or drop it)" allowance.
    return null;
  }
  return orgName;
};

// Splits the whole GET /api/bookings/my response into one card per
// non-recurring booking and one card per recurring series, then into
// Upcoming (soonest first) and Past (newest first) sections, split at
// `now`. A series counts as Upcoming as long as it has any occurrence at
// or after `now`; once every occurrence is behind `now` it moves to Past.
export const groupMyBookings = (bookings, now = new Date()) => {
  const list = Array.isArray(bookings) ? bookings : [];
  const seriesGroups = new Map();
  const singles = [];

  list.forEach((booking) => {
    const key = buildSeriesKey(booking);
    if (key) {
      if (!seriesGroups.has(key)) seriesGroups.set(key, []);
      seriesGroups.get(key).push(booking);
    } else {
      singles.push(booking);
    }
  });

  const cards = [];

  seriesGroups.forEach((seriesBookings, key) => {
    const {
      representative, occurrences, isOpenEnded, isTruncated,
    } = buildSeriesEntry(seriesBookings);
    const { occurrence: nextOccurrence, isEnded } = pickNextOccurrence(occurrences, now);
    cards.push({
      kind: 'series',
      key,
      id: representative.id,
      representative,
      roomName: representative.room_name || `Room ${representative.room_id}`,
      purpose: representative.purpose || null,
      syncError: representative.sync_status === 'error',
      isOpenEnded,
      isTruncated,
      occurrences,
      nextOccurrence,
      isEnded,
      sortStart: (isEnded ? occurrences[occurrences.length - 1] : nextOccurrence)?.start
        || new Date(representative.start_time),
    });
  });

  singles.forEach((booking) => {
    const start = new Date(booking.start_time);
    const end = booking.end_time ? new Date(booking.end_time) : null;
    const isEnded = end ? end.getTime() < now.getTime() : start.getTime() < now.getTime();
    cards.push({
      kind: 'single',
      key: `single:${booking.id}`,
      id: booking.id,
      booking,
      roomName: booking.room_name || `Room ${booking.room_id}`,
      start,
      end,
      isEnded,
      statusKey: resolveDisplayStatus(booking).key,
      statusText: getStatusText(booking),
      statusBadgeClass: getStatusBadgeClass(booking),
      sortStart: start,
    });
  });

  const upcoming = cards.filter((c) => !c.isEnded).sort((a, b) => a.sortStart - b.sortStart);
  const past = cards.filter((c) => c.isEnded).sort((a, b) => b.sortStart - a.sortStart);

  return { upcoming, past };
};
